import { format } from "date-fns";
import {
	type ActorRefFrom,
	type ActorRefFromLogic,
	assign,
	enqueueActions,
	fromPromise,
	not,
	sendParent,
	sendTo,
	setup,
} from "xstate";

import { docsApi, getDocsApiUrl, getDocsApiUrlWithoutProtocol } from "~/state/api/docsapi";

import { LOCAL_WORKSPACE_NAME } from "../../../strings";
import { errorMessage } from "../../../utils/helpers/error";
import { type AuthenticationMachine } from "../authentication";
import { type BaseMachine } from "../base/base.machine";
import { type BaseMachineEventsEmitted } from "../base/types";
import { createRemoteRepo } from "../docsapi/createRemoteRepo";
import { setRepoPublished } from "../docsapi/setRepoPublished";
import { GIT_PUBLISHING_BRANCH_NAME, GIT_PUBLISHING_REMOTE_NAME } from "../git/addGitMomentRemote";
import { type gitRepositoryMachine } from "../git/repository/git-repository.machine";
import { type GitRepositoryMachineEmitted } from "../git/repository/git-repository.types";
import { revalidateDocument } from "../git/revalidateDocument";
import {
	type SyncAction,
	type SyncMode,
	type SyncPlan,
	actionIsPlanned,
	emptySyncPlan,
} from "./plans";
import { syncPlannerMachine } from "./syncPlanner.machine";

export interface SyncMachineInputs {
	workspaceName: string;
	documentId: string;
	documentPath: string;
}

export interface SyncMachineArgs extends SyncMachineInputs {
	authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
	baseActorRef: ActorRefFromLogic<BaseMachine>;
}

export interface SyncMachineContext extends SyncMachineInputs {
	publishWorkspace?: string;
	trySyncPlan: SyncPlan;
	publishIfUnpublishedPlan: SyncPlan;
	shareSyncPlan: SyncPlan;
	executingSyncPlan: SyncPlan | undefined;

	syncMode?: SyncMode;
	pendingCommitMessage?: string;

	// passed in refs from parent
	authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
	baseActorRef: ActorRefFromLogic<BaseMachine>;

	provisioningPublishUrlRetryCount?: number;

	error?: string;
}

export type SyncRegeneratePlanEvent = {
	type: "sync.regeneratePlan";
	//workspaceName: string;
};

export type SyncExecutePlanEvent = {
	type: "sync.executePlan";
	pendingCommitMessage?: string;
	syncMode: SyncMode;
};

export type SyncProvisioningPublishUrlRetryEvent = {
	type: "sync.provisioningPublishUrl.retry";
};

export type SyncUnpublishDocumentEvent = {
	type: "sync.unpublishDocument";
};

export type BaseActorChangedEvent = {
	type: "sync.baseActorChanged";
	baseActorRef: ActorRefFromLogic<BaseMachine>;
};

export type GitActorChangedEvent = {
	type: "sync.gitActorChanged";
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
};

// this is needed as when we refresh a document,
// the workspace does not get propagated until all the gitea queries from the document list is returned.
// this is a bug, but a more invasive fix.
// Until we redo DocsList, an easier fix is for document machines to send an update if the remote workspaces are updated.
export type ResyncWorkspace = {
	type: "sync.resyncWorkspace";
	workspace: string;
};

export type AssignWorkspace = {
	type: "sync.assignWorkspace";
	workspaceName: string;
};

// const debuglog = (_msg: string, ...args: unknown[]) =>
// 	console.info(`[syncMachine] ${_msg}`, ...args);

const debuglog = (_msg: string, ..._args: unknown[]) => {};

const getPlan = (plan: SyncMode, context: SyncMachineContext): SyncPlan => {
	switch (plan) {
		case "try-sync":
			return context.trySyncPlan;
		case "publish-if-unpublished":
			return context.publishIfUnpublishedPlan;
		case "share-sync":
			return context.shareSyncPlan;
		default:
			throw new Error(`Invalid sync mode ${plan}`);
	}
};

export const syncMachine = setup({
	types: {
		input: {} as SyncMachineArgs,
		context: {} as SyncMachineContext,
		events: {} as
			| GitRepositoryMachineEmitted
			| BaseMachineEventsEmitted
			| SyncExecutePlanEvent
			| SyncUnpublishDocumentEvent
			| SyncRegeneratePlanEvent
			| ResyncWorkspace
			| AssignWorkspace
			| BaseActorChangedEvent
			| GitActorChangedEvent
			| SyncProvisioningPublishUrlRetryEvent,
	},
	actors: {
		planQuerier: syncPlannerMachine,
		createRemoteRepo: fromPromise(createRemoteRepo),
		setRepoPublished: fromPromise(setRepoPublished),
		revalidateDocument: fromPromise(revalidateDocument),

		// this checks if the remote document exists. if not,
		// it will throw an error.
		// use only first creation
		provisioningPublishUrl: fromPromise(
			async ({
				input: { publishWorkspace, documentId, authenticationActorRef, skip },
			}: {
				input: {
					publishWorkspace: string;
					documentId: string;
					authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
					skip: boolean;
				};
			}) => {
				if (skip) {
					return;
				}

				const exists = await docsApi.document.remoteExists(
					publishWorkspace,
					documentId,
					authenticationActorRef
				);
				if (!exists) {
					throw new Error(
						`Remote document does not exist for workspace ${publishWorkspace} and document ${documentId}`
					);
				}
			}
		),
	},

	guards: {
		baseActorReady: ({ context }) => {
			return (
				context.baseActorRef !== undefined &&
				context.baseActorRef.getSnapshot().matches("running")
			);
		},
		ifPlanned: ({ context }, params: { step: SyncAction }) =>
			actionIsPlanned(context.executingSyncPlan, params.step),
		ifNotPlanned: ({ context }, params: { step: SyncAction }) =>
			!actionIsPlanned(context.executingSyncPlan, params.step),
		validPublishWorkspace: ({ context }) => {
			return (
				context.publishWorkspace !== undefined &&
				context.publishWorkspace !== "" &&
				context.publishWorkspace !== LOCAL_WORKSPACE_NAME
			);
		},
		isAchievable: ({ context }, params: { syncMode: SyncMode }) => {
			const { planStatus, plannedOutcome } = getPlan(params.syncMode, context);

			debuglog(`isAchievable: planStatus=${planStatus} plannedOutcome=${plannedOutcome}`);

			return (
				planStatus === "achievable" ||
				(planStatus === "unachievable-not-authenticated" && plannedOutcome === "commit") ||
				(planStatus === "unachievable-not-on-synced-branch" && plannedOutcome === "commit")
			);
		},
		isPublished: ({ context }) =>
			context.publishIfUnpublishedPlan.publishStatus === "published",
	},
	actions: {
		assignError: assign((_, params: { prefix: string; error: unknown }) => {
			const error = `${params.prefix}: ${errorMessage(params.error)}`;
			console.error(error);
			return { error };
		}),
	},
	delays: {
		timeout: ({ context }) => {
			return (context.provisioningPublishUrlRetryCount ?? 1) * 1000;
		},
	},
}).createMachine({
	id: "syncMachine",
	context: ({ input }) => {
		return {
			...input,

			trySyncPlan: emptySyncPlan(),
			publishIfUnpublishedPlan: emptySyncPlan(),
			shareSyncPlan: emptySyncPlan(),
			executingSyncPlan: undefined,
			publishWorkspace: undefined,
		};
	},
	on: {
		"git.commit.done": {
			target: ".generatingPlan",
		},
	},
	initial: "generatingPlan",
	states: {
		generatingPlan: {
			entry: [() => debuglog("generatingPlan")],
			invoke: {
				src: "planQuerier",
				input: ({ context }) => ({ ...context }),
				onDone: {
					actions: enqueueActions(({ enqueue, event }) => {
						enqueue.assign({
							publishWorkspace: undefined,
							trySyncPlan: emptySyncPlan(),
							publishIfUnpublishedPlan: emptySyncPlan(),
							shareSyncPlan: emptySyncPlan(),
							executingSyncPlan: undefined,

							syncMode: undefined,
							pendingCommitMessage: undefined,
						});

						debuglog("planQuerier done", { output: event.output });

						enqueue.assign(({ event }) => ({ ...event.output }));
						enqueue(({ event }) =>
							debuglog(
								`new plan generated! workspace=${event.output.publishWorkspace}`,
								event.output
							)
						);
					}),
					target: "idle",
				},
			},
		},

		idle: {
			entry: [() => debuglog("idle")],
			on: {
				"sync.baseActorChanged": {
					actions: assign(({ event, context }) => ({
						baseActorRef: event.baseActorRef ?? context.baseActorRef,
					})),
					target: "idle",
				},
				"sync.gitActorChanged": {
					actions: assign(({ event, context }) => ({
						gitRef: event.gitRef ?? context.gitRef,
					})),
					target: "idle",
				},
				"sync.regeneratePlan": {
					target: "generatingPlan",
				},
				"sync.executePlan": {
					guard: {
						type: "isAchievable",
						params: ({ event }) => ({ syncMode: event.syncMode }),
					},
					actions: [
						() => debuglog("executing syncPlan!"),
						assign(({ event, context }) => ({
							error: undefined,
							pendingCommitMessage: event.pendingCommitMessage,
							executingSyncPlan: getPlan(event.syncMode, context),
						})),
					],
					target: "creatingRemoteRepo",
				},
				"sync.unpublishDocument": {
					guard: "isPublished",
					target: "settingRemoteRepoToUnpublished",
					actions: [() => debuglog("unpublishing document!")],
				},
				"sync.resyncWorkspace": {
					actions: [
						assign(({ event }) => ({
							publishWorkspace: event.workspace,
						})),
						() => debuglog("resyncing workspace!"),
					],
					target: "generatingPlan",
				},
				"sync.assignWorkspace": {
					actions: [
						assign(({ event }) => ({
							workspaceName: event.workspaceName,
						})),
						() => debuglog("assigning workspace!"),
					],
					target: "generatingPlan",
				},
			},
			exit: [({ event }) => debuglog("exiting idle", { event })],
		},

		creatingRemoteRepo: {
			entry: [
				({ context }) =>
					debuglog("creatingRemoteRepo with workspace ", {
						workspace: context.publishWorkspace,
					}),
			],
			invoke: {
				src: "createRemoteRepo",
				input: ({ context }) => ({
					documentId: context.documentId,
					workspaceName: context.publishWorkspace ?? "",
					authenticationActorRef: context.authenticationActorRef,
					skip:
						!actionIsPlanned(context.executingSyncPlan, "create-publish-repo") ||
						context.authenticationActorRef.getSnapshot().context.authnStatus !==
							"authenticated",
				}),
				onDone: {
					target: "initializingLocalRepo",
					actions: [
						() => debuglog("creatingRemoteRepo DONE"),
						sendParent(({ context }) => ({
							type: "document.remoteCreated.addWorkspace",
							workspace: {
								id: `${getDocsApiUrlWithoutProtocol()}/${context.publishWorkspace}`,
								name: context.publishWorkspace,
								host: getDocsApiUrlWithoutProtocol(),
								slug: context.publishWorkspace,
							},
						})),
					],
				},
				onError: {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "creatingRemoteRepo ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
		},

		initializingLocalRepo: {
			entry: enqueueActions(({ enqueue, check }) => {
				enqueue(() => debuglog("initializingLocalRepo"));
				if (check({ type: "ifPlanned", params: { step: "initialize-git-repo" } })) {
					enqueue(
						sendTo(
							({ context }) => context.gitRef,
							// @ts-ignore self is an ActorRef but sender does not like it
							({ self }) => ({ type: "git.init", sender: self })
						)
					);
				}
			}),
			always: {
				guard: { type: "ifNotPlanned", params: { step: "initialize-git-repo" } },
				target: "committingOutstandingChanges",
				actions: [() => debuglog("initializingLocalRepo SKIPPING")],
			},
			on: {
				"git.init.done": {
					target: "committingOutstandingChanges",
					actions: [() => debuglog("initializingLocalRepo DONE")],
				},
				"git.init.error": {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "initializingLocalRepo ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("initializingLocalRepo TIMEOUT (resetting)")],
				},
			},
		},

		committingOutstandingChanges: {
			entry: enqueueActions(({ enqueue, check }) => {
				enqueue(() => debuglog("committingOutstandingChanges"));
				if (check({ type: "ifPlanned", params: { step: "commit-outstanding-changes" } })) {
					enqueue(
						sendTo(
							({ context }) => context.gitRef,
							// @ts-ignore self is an ActorRef but sender does not like it
							({ context, self }) => ({
								type: "git.commit",
								payload: {
									message:
										context.pendingCommitMessage ??
										`Publishing changes ${format(new Date(), "yyyy/MM/dd")}`,
								},
								sender: self,
							})
						)
					);
				}
			}),
			always: {
				guard: { type: "ifNotPlanned", params: { step: "commit-outstanding-changes" } },
				target: "creatingMomentRemote",
				actions: [() => debuglog("committingOutstandingChanges SKIPPING")],
			},
			on: {
				"git.commit.done": {
					target: "creatingMomentRemote",
					actions: [() => debuglog("committingOutstandingChanges DONE")],
				},
				"git.commit.error": {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => {
								console.error("git.commit.error", event, event.error);
								return {
									prefix: "committingOutstandingChanges ERROR (resetting)",
									error: event.error,
								};
							},
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("committingOutstandingChanges TIMEOUT (resetting)")],
				},
			},
		},

		creatingMomentRemote: {
			entry: enqueueActions(({ enqueue, check }) => {
				enqueue(() => debuglog("creatingMomentRemote"));
				if (
					check({ type: "ifPlanned", params: { step: "create-remote" } }) &&
					check({ type: "validPublishWorkspace" })
				) {
					enqueue(
						sendTo(
							({ context }) => context.gitRef,
							// @ts-ignore self is an ActorRef but sender does not like it
							({ context, self }) => ({
								type: "git.remote.add",
								payload: {
									remote: GIT_PUBLISHING_REMOTE_NAME,
									url: `${getDocsApiUrl()}/git/${context.publishWorkspace}/${context.documentId}.git`,
								},
								sender: self,
							})
						)
					);
				}
			}),
			always: [
				{
					guard: not({ type: "validPublishWorkspace" }),
					target: "generatingPlan",
					actions: [() => debuglog("creatingMomentRemote INVALID UPSTREAM (resetting)")],
				},
				{
					guard: { type: "ifNotPlanned", params: { step: "create-remote" } },
					target: "settingMomentTrackingBranch",
					actions: [() => debuglog("creatingMomentRemote SKIPPING")],
				},
			],
			on: {
				"git.remote.add.done": {
					target: "settingMomentTrackingBranch",
					actions: [() => debuglog("creatingMomentRemote DONE")],
				},
				"git.remote.add.error": {
					target: "processRemoteAddError",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "creatingMomentRemote ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("creatingMomentRemote TIMEOUT (resetting)")],
				},
			},
		},

		processRemoteAddError: {
			entry: () => debuglog("processRemoteAddError"),
			always: [
				{
					guard: ({ event }) => {
						if (event.type !== "git.remote.add.error") {
							console.error("Unexpected event in proccessRemoteAddError", event);
							return false;
						}

						console.error("processRemoteAddError", event.error);

						if (event.error === undefined) {
							debuglog("this should not be possible");
							return false;
						}
						return event.error.includes(
							"error from git: remote 'moment' already exists"
						);
					},
					actions: [
						() =>
							debuglog(
								"processRemoteAddError remote exists. clearing error and continuing"
							),
						assign({
							error: undefined,
						}),
					],
					target: "settingMomentTrackingBranch",
				},
				{
					target: "generatingPlan",
				},
			],
		},

		settingMomentTrackingBranch: {
			entry: enqueueActions(({ enqueue, check }) => {
				enqueue(() => debuglog("settingMomentTrackingBranch"));
				if (check({ type: "ifPlanned", params: { step: "set-tracking-branch" } })) {
					enqueue(
						sendTo(
							({ context }) => context.gitRef,
							// @ts-ignore self is an ActorRef but sender does not like it
							({ self }) => ({
								type: "git.branch.track",
								payload: {
									remote: GIT_PUBLISHING_REMOTE_NAME,
									localBranch: GIT_PUBLISHING_BRANCH_NAME,
									upstreamBranch: GIT_PUBLISHING_BRANCH_NAME,
								},
								sender: self,
							})
						)
					);
				}
			}),
			always: {
				guard: { type: "ifNotPlanned", params: { step: "set-tracking-branch" } },
				target: "pushingToRemote",
				actions: [() => debuglog("settingMomentTrackingBranch SKIPPING")],
			},
			on: {
				"git.branch.track.done": {
					target: "pushingToRemote",
					actions: [() => debuglog("settingMomentTrackingBranch DONE")],
				},
				"git.branch.track.error": {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "settingMomentTrackingBranch ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("settingMomentTrackingBranch TIMEOUT (resetting)")],
				},
			},
		},

		pushingToRemote: {
			entry: enqueueActions(({ enqueue, check }) => {
				enqueue(() => debuglog("pushingToRemote"));
				if (check({ type: "ifPlanned", params: { step: "push-to-remote" } })) {
					enqueue(
						sendTo(
							({ context }) => context.gitRef,
							// @ts-ignore self is an ActorRef but sender does not like it
							({ self }) => ({
								type: "git.push",
								payload: {
									remote: GIT_PUBLISHING_REMOTE_NAME,
									branch: GIT_PUBLISHING_BRANCH_NAME,
								},
								sender: self,
							})
						)
					);
				}
			}),
			always: {
				guard: { type: "ifNotPlanned", params: { step: "push-to-remote" } },
				target: "settingRemoteRepoToPublished",
				actions: [() => debuglog("pushingToRemote SKIPPING")],
			},
			on: {
				"git.push.done": {
					target: "settingRemoteRepoToPublished",
					actions: [() => debuglog("pushingToRemote DONE")],
				},
				"git.push.error": {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "pushingToRemote ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("pushingToRemote TIMEOUT (resetting)")],
				},
			},
		},

		settingRemoteRepoToPublished: {
			entry: [() => debuglog("settingRemoteRepoToPublished")],
			always: {
				guard: { type: "ifNotPlanned", params: { step: "publish-document" } },
				target: "ensuringRebootTablesCreated",
				actions: [() => debuglog(" SKIPPING")],
			},
			invoke: {
				src: "setRepoPublished",
				input: ({ context }) => ({
					type: "grant",
					relation: "reader",
					documentId: context.documentId,
					publishWorkspace: context.publishWorkspace ?? "",
					authenticationActorRef: context.authenticationActorRef,
					skip: !actionIsPlanned(context.executingSyncPlan, "publish-document"),
				}),
				onDone: {
					target: "ensuringRebootTablesCreated",
					actions: [() => debuglog("settingRemoteRepoToPublished DONE")],
				},
				onError: {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "settingRemoteRepoToPublished ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("settingRemoteRepoToPublished TIMEOUT (resetting)")],
				},
			},
		},

		ensuringRebootTablesCreated: {
			entry: [
				() => debuglog("ensuringRebootTablesCreated"),
				enqueueActions(({ context, check }) => {
					debuglog("ensuringRebootTablesCreated", {
						publishWorkspace: context.publishWorkspace,
					});
					if (context.publishWorkspace === undefined) {
						debuglog("ensuringRebootTablesCreated SKIPPING (no publish workspace)");
						throw new Error(
							"ensuringRebootTablesCreated SKIPPING (no publish workspace)"
						);
					}

					if (
						check({
							type: "ifPlanned",
							params: { step: "ensure-reboot-tables-created" },
						}) &&
						check({
							type: "baseActorReady",
						})
					) {
						context.baseActorRef.send({
							type: "base.publish",
							workspaceName: context.publishWorkspace,
						});
					}
				}),
			],
			always: {
				guard: { type: "ifNotPlanned", params: { step: "ensure-reboot-tables-created" } },
				target: "revalidatingDocument",
				actions: [() => debuglog("ensuringRebootTablesCreated SKIPPING")],
			},
			on: {
				"base.publishedToReboot": {
					target: "settingRemoteRepoToPublished",
					actions: [() => debuglog("ensuringRebootTablesCreated DONE")],
				},
			},
			after: {
				10_000: {
					target: "generatingPlan",
					actions: [() => debuglog("ensuringRebootTablesCreated TIMEOUT (resetting)")],
				},
			},
		},

		revalidatingDocument: {
			entry: [() => debuglog("revalidatingDocument")],
			always: {
				guard: { type: "ifNotPlanned", params: { step: "revalidate-document" } },
				// If we are not revalidating, we should not need to provision a publish URL.
				target: "generatingPlan",
				actions: [() => debuglog(" SKIPPING")],
			},
			guard: "validPublishWorkspace",
			invoke: {
				src: "revalidateDocument",
				input: ({ context }) => ({
					...context,
					publishWorkspace: context.publishWorkspace ?? "",
					skip: !actionIsPlanned(context.executingSyncPlan, "revalidate-document"),
				}),
				onDone: {
					target: "provisioningPublishUrl",
					actions: [() => debuglog("revalidatingDocument DONE")],
				},
				onError: {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "revalidatingDocument ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("revalidatingDocument TIMEOUT (resetting)")],
				},
			},
		},

		provisioningPublishUrl: {
			entry: [({ context }) => debuglog("provisioningPublishUrl", { context })],
			exit: [() => debuglog("DONE provisioningPublishUrl")],

			on: {
				"sync.provisioningPublishUrl.retry": {
					actions: [
						() => debuglog("sync.provisioningPublishUrl.retry"),
						assign(({ context }) => ({
							provisioningPublishUrlRetryCount:
								(context.provisioningPublishUrlRetryCount ?? 0) + 1,
						})),
					],
					after: {
						timeout: {
							target: "provisioningPublishUrl",
						},
					},
				},
				// if we get any other error,
				// move on to regenerate the plan.
				"sync.regeneratePlan": {
					actions: [
						() => debuglog("sync.regeneratePlan"),
						assign({
							provisioningPublishUrlRetryCount: 0,
						}),
					],
					target: "generatingPlan",
				},
			},
			invoke: {
				src: "provisioningPublishUrl",
				input: ({ context }) => ({
					publishWorkspace: context.publishWorkspace ?? "",
					documentId: context.documentId,
					authenticationActorRef: context.authenticationActorRef,
					skip: !actionIsPlanned(context.executingSyncPlan, "provisioning-publish-url"),
				}),
				onDone: {
					actions: [
						() => debuglog("provisioningPublishUrl DONE"),
						assign(({ context }) => ({
							provisioningPublishUrlRetryCount: 0,
							error: undefined,
						})),
					],
					target: "generatingPlan",
				},
				onError: {
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "provisioningPublishUrl ERROR",
								error: event.error,
							}),
						},
						enqueueActions(({ enqueue, context, event }) => {
							debuglog("provisioningPublishUrl ERROR", {
								error: event.error,
								retryCount: context.provisioningPublishUrlRetryCount,
							});
							const err = event.error as Error;
							if (
								err.message.includes("Remote document does not exist") &&
								(context.provisioningPublishUrlRetryCount ?? 0) < 3
							) {
								enqueue.emit({
									type: "sync.provisioningPublishUrl.retry",
								} as SyncProvisioningPublishUrlRetryEvent);
								return;
							}

							enqueue.emit({
								type: "sync.regeneratePlan",
							} as SyncRegeneratePlanEvent);
						}),
					],
				},
			},
		},

		settingRemoteRepoToUnpublished: {
			entry: [() => debuglog("settingRemoteRepoToUnpublished")],
			invoke: {
				src: "setRepoPublished",
				input: ({ context }) => ({
					type: "revoke",
					relation: "reader",
					documentId: context.documentId,
					publishWorkspace: context.publishWorkspace ?? "",
					authenticationActorRef: context.authenticationActorRef,
					skip: false,
				}),
				onDone: {
					target: "generatingPlan",
					actions: [() => debuglog("settingRemoteRepoToUnpublished DONE")],
				},
				onError: {
					target: "generatingPlan",
					actions: [
						{
							type: "assignError",
							params: ({ event }) => ({
								prefix: "settingRemoteRepoToUnpublished ERROR (resetting)",
								error: event.error,
							}),
						},
					],
				},
			},
			after: {
				5_000: {
					target: "generatingPlan",
					actions: [() => debuglog("settingRemoteRepoToUnpublished TIMEOUT (resetting)")],
				},
			},
		},
	},
});
